/**
 * Shared Arabic/bidi support for the jsPDF exporters.
 *
 * jsPDF draws glyphs left-to-right in string order and runs no bidi pass, so
 * Arabic must be shaped and handed over in visual order. The one invariant every
 * exporter must keep: wrap on the LOGICAL text first, convert each line to
 * visual order only at draw time — wrapping an already-reordered string puts
 * the paragraph's last words on the first line.
 */
import { jsPDF } from "jspdf";
import { detectDirection, render, type BaseDirection } from "bidi-shaper";
import amiriBoldUrl from "@/assets/fonts/Amiri-Bold.ttf?url";
import amiriRegularUrl from "@/assets/fonts/Amiri-Regular.ttf?url";

export const ARABIC_FONT = "Amiri";

/**
 * jsPDF runs TWO of its own Arabic/bidi passes over every string handed to
 * `text()`, and both destroy output that is already shaped and reordered:
 *
 * 1. `processArabic` on `preProcessText` re-scans our shaped text and fuses any
 *    lam that happens to sit beside an alef in VISUAL order into a lam-alef
 *    ligature — letters that were never adjacent logically. "مرحبا بالعالم"
 *    loses two glyphs that way. It is drawing-only (`getTextWidth` and
 *    `splitTextToSize` skip the event), so measurements silently disagree with
 *    the page and right-aligned RTL drifts.
 *
 * 2. `bidiEngineFunction` on `postProcessText` runs the bidi algorithm with
 *    `isInputVisual: true` and logical output, i.e. it converts our visual
 *    order BACK to logical order, which is then painted left to right. This is
 *    the one that makes Arabic read backwards on the page:
 *      in:  ةولح_ةايحلاو_اماع_#20   (correct visual order)
 *      out: 20#_عاما_والحياة_حلوة  (logical order — drawn LTR)
 *
 * We pre-shape and pre-order everything, so both passes are pure corruption.
 * Unsubscribe them once, before any document is constructed.
 *
 * Detection is BEHAVIOURAL, not by function name or source text, so it still
 * works after a production build mangles both. Note the sibling
 * `utf8EscapeFunction` on the same event is essential — it encodes the string
 * for output — so it must survive: it is told apart by the fact that it
 * replaces the text with a hex string rather than reordering the letters.
 */
function disableJsPdfTextRewriters(): void {
  const events = jsPDF.API.events as [string, (payload: unknown) => void][];
  if (!Array.isArray(events)) return;

  const arabic = (jsPDF.API as { processArabic?: unknown }).processArabic;
  const visualProbe = render("#20_\u0639\u0627\u0645\u0627");

  for (let i = events.length - 1; i >= 0; i -= 1) {
    const [topic, handler] = events[i] ?? [];
    if (!handler) continue;

    if (topic === "preProcessText" && arabic && handler === arabic) {
      events.splice(i, 1);
      continue;
    }

    if (topic !== "postProcessText") continue;
    const payload = { text: visualProbe, x: 0, y: 0, options: {}, mutex: {} };
    try {
      handler(payload);
    } catch {
      continue;
    }
    const after = Array.isArray(payload.text)
      ? (payload.text as unknown[]).flat().join("")
      : payload.text;
    // Reordered our letters instead of encoding them ⇒ it is the bidi engine.
    if (after !== visualProbe && ARABIC_RE.test(String(after))) {
      events.splice(i, 1);
    }
  }
}

const ARABIC_RE =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/;

disableJsPdfTextRewriters();

export function hasArabic(text: string): boolean {
  return ARABIC_RE.test(text);
}

export function baseDirection(text: string): "rtl" | "ltr" {
  return detectDirection(text) === "rtl" ? "rtl" : "ltr";
}

/**
 * First-strong isolate. Wrapping a self-contained token (a hashtag, a handle, a
 * code span) makes the bidi algorithm resolve its direction from its OWN first
 * strong character instead of the surrounding sentence. Without it, an Arabic
 * hashtag inside an English line — `#20_عاما_والحياة_حلوة` — inherits the
 * sentence's LTR base, and its `#20_` prefix resolves to the LTR level and is
 * placed at the far LEFT, i.e. the END of the token in Arabic reading order.
 * This is what `dir="auto"` does for an inline element in HTML.
 */
export const ISOLATE_START = "\u2068";
export const ISOLATE_END = "\u2069";

/** Bidi controls steer layout but must never reach a glyph — they have none. */
const BIDI_CONTROLS = /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/g;

export function stripBidiControls(text: string): string {
  return text.replace(BIDI_CONTROLS, "");
}

export function isBidiControl(ch: string): boolean {
  return ch.length === 1 && /[\u200E\u200F\u202A-\u202E\u2066-\u2069]/.test(ch);
}

export function toVisual(text: string, direction: BaseDirection = "auto"): string {
  return stripBidiControls(render(text, { direction }));
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

let fontCache: Promise<{ regular: string; bold: string }> | null = null;

function loadArabicFonts(): Promise<{ regular: string; bold: string }> {
  fontCache ??= Promise.all([
    fetch(amiriRegularUrl).then((r) => {
      if (!r.ok) throw new Error("Failed to load Amiri Regular");
      return r.arrayBuffer();
    }),
    fetch(amiriBoldUrl).then((r) => {
      if (!r.ok) throw new Error("Failed to load Amiri Bold");
      return r.arrayBuffer();
    }),
  ])
    .then(([regular, bold]) => ({
      regular: arrayBufferToBase64(regular),
      bold: arrayBufferToBase64(bold),
    }))
    .catch((error) => {
      fontCache = null;
      throw error;
    });
  return fontCache;
}

export async function ensureArabicFont(doc: jsPDF): Promise<void> {
  const fonts = await loadArabicFonts();
  doc.addFileToVFS("Amiri-Regular.ttf", fonts.regular);
  doc.addFileToVFS("Amiri-Bold.ttf", fonts.bold);
  doc.addFont("Amiri-Regular.ttf", ARABIC_FONT, "normal");
  doc.addFont("Amiri-Bold.ttf", ARABIC_FONT, "bold");
}

export function setArabicFont(doc: jsPDF, bold: boolean, sizePt: number): void {
  doc.setFont(ARABIC_FONT, bold ? "bold" : "normal");
  doc.setFontSize(sizePt);
}

/** Width of logical text once shaped — controls are dropped, they draw nothing. */
function measureLogical(
  doc: jsPDF,
  text: string,
  direction: BaseDirection,
): number {
  return doc.getTextWidth(stripBidiControls(render(text, { direction })));
}

function hardBreak(
  doc: jsPDF,
  word: string,
  maxWidth: number,
  direction: BaseDirection,
): string[] {
  const parts: string[] = [];
  let part = "";
  for (const ch of word) {
    const next = part + ch;
    if (part && measureLogical(doc, next, direction) > maxWidth) {
      parts.push(part);
      part = ch;
    } else {
      part = next;
    }
  }
  if (part) parts.push(part);
  return parts;
}

/**
 * Greedy word wrap of logical text, measured on the shaped visual form with the
 * doc's current font. Returns LOGICAL lines — convert with toVisual to draw.
 */
export function wrapBidi(doc: jsPDF, text: string, maxWidth: number): string[] {
  const direction = baseDirection(text);
  const lines: string[] = [];
  for (const para of text.split("\n")) {
    const words = para.split(/\s+/).filter(Boolean);
    if (!words.length) {
      lines.push("");
      continue;
    }
    const spaceW = doc.getTextWidth(" ");
    let line = "";
    let lineW = 0;
    const startWith = (word: string, width: number) => {
      if (width <= maxWidth) {
        line = word;
        lineW = width;
        return;
      }
      const parts = hardBreak(doc, word, maxWidth, direction);
      for (let i = 0; i < parts.length - 1; i += 1) lines.push(parts[i]!);
      line = parts[parts.length - 1] ?? "";
      lineW = line ? measureLogical(doc, line, direction) : 0;
    };
    for (const word of words) {
      const w = measureLogical(doc, word, direction);
      if (!line) {
        startWith(word, w);
      } else if (lineW + spaceW + w > maxWidth) {
        lines.push(line);
        startWith(word, w);
      } else {
        line += ` ${word}`;
        lineW += spaceW + w;
      }
    }
    if (line) lines.push(line);
  }
  return lines;
}
